import * as Sentry from "@sentry/react";

// Only initialize in production builds or when DSN is explicitly set
const DSN = import.meta.env.VITE_SENTRY_DSN ||
  "https://10dd6635d8b6207bffdabb3ff5af7225@o4511098003456000.ingest.de.sentry.io/4511098019512400";

const isProd = import.meta.env.PROD;

if (isProd) {
  Sentry.init({
    dsn: DSN,

    environment: import.meta.env.VITE_APP_ENV || "production",
    release:     import.meta.env.VITE_APP_VERSION || "studox-flow@3.0.0",

    // ── Performance monitoring ──────────────────────────────────────────────
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText:   false, // Set true for stricter privacy
        blockAllMedia: false,
      }),
    ],

    // Capture 20% of transactions for performance data
    tracesSampleRate: 0.2,

    // Record 10% of sessions, 100% when an error occurs
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // ── Filter out noise ────────────────────────────────────────────────────
    ignoreErrors: [
      // Benign network/browser errors
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "Network request failed",
      "Failed to fetch",
      "NetworkError",
      "AbortError",
      // React Router navigation cancellations
      "Invariant: attempted to hard navigate to the same URL",
    ],
    denyUrls: [
      // Chrome/Firefox extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // ── User context (attached automatically from auth) ─────────────────────
    beforeSend(event) {
      // Strip any PII from breadcrumbs
      if (event.breadcrumbs?.values) {
        event.breadcrumbs.values = event.breadcrumbs.values.map((b) => {
          if (b.category === "xhr" || b.category === "fetch") {
            // Don't log request bodies
            if (b.data?.body) b.data.body = "[redacted]";
          }
          return b;
        });
      }
      return event;
    },
  });
}

/** Call after login to attach user identity to Sentry events */
export function sentryIdentifyUser(user) {
  if (!isProd || !user) return;
  Sentry.setUser({
    id:   user.id,
    role: user.role,
    // Never include name/email in Sentry — GDPR
  });
}

/** Call after logout */
export function sentryClearUser() {
  if (!isProd) return;
  Sentry.setUser(null);
}

/** Wrap a React component with Sentry error boundary */
export { Sentry };
